import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import { PASSWORD_ERRORS, validateConfirmationPassword, createValidatePasswordWithExtraRestrictedWords } from '@Utils/Password';
import ProfileSettingsAccordionItem from '@Pages/ProfileSettings/Components/ProfileSettingsAccordionItem';
import PasswordField from './PasswordField';

const FIELD_NAMES = {
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  PASSWORD: 'profile-settings-account-password',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  NEW_PASSWORD: 'profile-settings-account-new-password',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  CONFIRM_PASSWORD: 'profile-settings-account-confirm-password',
};

const passwordErrorsMessages = {
  [PASSWORD_ERRORS.REQUIRED]: 'Please choose a new password.',
  [PASSWORD_ERRORS.LOWER_CASE]: 'Password must have at least one lower case character.',
  [PASSWORD_ERRORS.UPPER_CASE]: 'Password must have at least one upper case character.',
  [PASSWORD_ERRORS.DIGIT_OR_SPECIAL]: 'Password must have at least one numeric character or special characters.',
  [PASSWORD_ERRORS.MIN_LENGTH]: 'Password must have minimum of 8 characters.',
  [PASSWORD_ERRORS.MAX_LENGTH]: 'Password must have maximum of 200 characters.',
  [PASSWORD_ERRORS.RESTRICT]: 'Password must not contain prohibited expression.',
  [PASSWORD_ERRORS.SEQUENCE]: 'Password must not contain sequential alpha numeric characters.',
  [PASSWORD_ERRORS.CHECK_ASCII]: 'Password must contain only printable, non-whitespace ASCII characters.',
  [PASSWORD_ERRORS.COMPARE_TO]: 'The two passwords given are not the same. Please check your spelling.',
  [PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]: 'Please confirm new password.',
};

function PasswordSection({ value: passwordData, onChange, onSave, onClose, restrictedWords, trackAdobeAnalyticsError }) {
  const [passwordFormErrors, setPasswordFormErrors] = useState(null);
  const [newPasswordFormErrors, setNewPasswordFormErrors] = useState(null);
  const [confirmPasswordFormErrors, setConfirmPasswordFormErrors] = useState(null);

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const { password, newPassword, confirmPassword } = passwordData;

  const validateNewPassword = useMemo(() => createValidatePasswordWithExtraRestrictedWords(restrictedWords), [restrictedWords]);

  const formValid = useMemo(() => {
    const isPasswordValid = !!password;
    const { isValid: isNewPasswordValid } = validateNewPassword(newPassword);
    const { isValid: isConfirmPasswordValid } = validateConfirmationPassword(newPassword, confirmPassword);

    return isPasswordValid && isNewPasswordValid && isConfirmPasswordValid;
  }, [confirmPassword, newPassword, password, validateNewPassword]);

  const validatePasswordField = (value) => {
    setPasswordFormErrors(value ? null : ['Please enter your current password.']);
    return !!value;
  };

  const validateNewPasswordField = (value) => {
    const { errors, isValid } = validateNewPassword(value);
    setNewPasswordFormErrors(isValid ? null : errors.map((error) => passwordErrorsMessages[error]));
    return isValid;
  };

  const validateConfirmPasswordField = (value, newPasswordValue) => {
    const { errors, isValid } = validateConfirmationPassword(newPasswordValue || newPassword, value);
    setConfirmPasswordFormErrors(isValid ? null : errors.map((error) => passwordErrorsMessages[error]));
    return isValid;
  };

  const handleInputChange = (key, value) => {
    onChange({ ...passwordData, [key]: value });
  };

  const handleInputBlur = (formErrors) => {
    trackAdobeAnalyticsError(formErrors);
  };

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    handleInputChange('password', value);
    validatePasswordField(value);
  };

  const handleNewPasswordChange = (e) => {
    const { value } = e.target;
    handleInputChange('newPassword', value);
    validateNewPasswordField(value);
    if (confirmPassword) {
      validateConfirmPasswordField(confirmPassword, value);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;
    handleInputChange('confirmPassword', value);
    validateConfirmPasswordField(value);
  };

  const handleClose = () => {
    setPasswordFormErrors(null);
    setNewPasswordFormErrors(null);
    setConfirmPasswordFormErrors(null);
    setIsFormSubmitted(false);
    onClose();
  };

  const handleSave = (callback) => {
    if (!isFormSubmitted) {
      setIsFormSubmitted(true);
      const isPasswordValid = validatePasswordField(passwordData.password);
      const isNewPasswordValid = validateNewPasswordField(passwordData.newPassword);
      const isConfirmPasswordValid = validateConfirmPasswordField(passwordData.confirmPassword, passwordData.newPassword);

      const isFormValid = isPasswordValid && isNewPasswordValid && isConfirmPasswordValid;

      if (!isFormValid) {
        return;
      }
    }

    onSave();
    callback();
  };

  return (
    <ProfileSettingsAccordionItem
      onClose={handleClose}
      label={ProfileSettingsConfig.Labels.password}
      value={ProfileSettingsConfig.FakePassword}
      title={ProfileSettingsConfig.LinkText.password}
      onSave={handleSave}
      formValid={formValid}
      headingLevel="h3"
      validateOnSave>
      <PasswordField
        key={FIELD_NAMES.PASSWORD}
        id={FIELD_NAMES.PASSWORD}
        name={FIELD_NAMES.PASSWORD}
        type="password"
        label={ProfileSettingsConfig.Labels.currentPassword}
        value={password}
        isSubmitted={isFormSubmitted}
        formErrors={passwordFormErrors}
        onChange={handlePasswordChange}
        onBlur={() => handleInputBlur(passwordFormErrors)}
      />

      <PasswordField
        name={FIELD_NAMES.NEW_PASSWORD}
        label={ProfileSettingsConfig.Labels.newPassword}
        value={newPassword}
        className="u-els-margin-top-1o2"
        onChange={handleNewPasswordChange}
        isSubmitted={isFormSubmitted}
        formErrors={newPasswordFormErrors}
        onBlur={() => handleInputBlur(newPasswordFormErrors)}
      />

      <PasswordField
        name={FIELD_NAMES.CONFIRM_PASSWORD}
        label={ProfileSettingsConfig.Labels.confirmPassword}
        value={confirmPassword}
        className="u-els-margin-top-1o2"
        onChange={handleConfirmPasswordChange}
        formErrors={confirmPasswordFormErrors}
        isSubmitted={isFormSubmitted}
        onBlur={() => handleInputBlur(confirmPasswordFormErrors)}
      />
    </ProfileSettingsAccordionItem>
  );
}

PasswordSection.propTypes = {
  value: PropTypes.shape({
    password: PropTypes.string,
    newPassword: PropTypes.string,
    confirmPassword: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  restrictedWords: PropTypes.arrayOf(PropTypes.string),
  trackAdobeAnalyticsError: PropTypes.func.isRequired,
};

export default PasswordSection;
